import React from "react";
import {
  makeStyles,
  List,
  Drawer,
  Button,
  Tabs,
  Tab,
  Paper,
  Typography,
} from "@material-ui/core";
import {
  Menu,
  ChevronLeft,
  DriveEta,
  TripOrigin,
  History,
  Add,
  AddLocation,
} from "@material-ui/icons";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";

import { StoreContext } from "../../store/StoreContext";
import {
  CollapseItem,
  TripItem,
  HistoryItem,
  Filter,
  Footer,
  IntervalsForm,
  DateFilter,
} from "./";
import { TabPanel } from "../UI/TabPanel";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    width: 0,
  },
  drawerButton: {
    zIndex: 401,
    position: "absolute",
    left: 20,
    top: 20,
    backgroundColor: "#fff",
  },
  drawerBackButton: {
    justifyContent: "flex-end",
  },
  tab: {
    minWidth: "100px",
  },
  drawerContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "86%",
    maxHeight: "100%",
    paddingBottom: 5,
  },
  itemsList: {
    height: "95%",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: 5,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#555",
    },
  },
  addPanel: {
    display: "flex",
    justifyContent: "center",
    marginTop: 10,
  },
}));

const AddPanel = ({ style }) => {
  const {
    modalStore: { setAddCarShowing, setAddDepartmentShowing },
  } = React.useContext(StoreContext);
  const { t } = useTranslation();

  return (
    <div className={style}>
      <Button
        variant="contained"
        color="secondary"
        endIcon={<Add />}
        style={{ marginRight: 10, textTransform: "none" }}
        size="small"
        onClick={() => setAddDepartmentShowing(true)}
      >
        {t("DEPARTMENT")}
      </Button>
      <Button
        variant="contained"
        color="secondary"
        endIcon={<AddLocation />}
        size="small"
        style={{ textTransform: "none" }}
        onClick={() => setAddCarShowing(true)}
      >
        {t("CAR")}
      </Button>
    </div>
  );
};

export const SideMenu = observer(({ changeTab }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    cars,
    departments,
    trips,
    models,
    searchHistory,
    currentTrips,
    setCurrentTrips,
    currentCars,
    setCurrentCars,
    setShowCars,
  } = React.useContext(StoreContext);
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState(0);

  React.useEffect(() => {
    document.title = "ACMon " + t("MAP");
  }, []);

  const filters = [
    { name: "model", localed: t("MODEL") },
    { name: "department", localed: t("DEPARTMENT") },
  ];
  const values = filters.reduce((acc, filter) => {
    const arr = [];
    let data = [];
    if (filter.name === "model") {
      data = models;
    } else if (filter.name === "department") {
      data = departments;
    }

    cars.forEach((car) => {
      const element = data.find((el) => el.id === car[filter.name]);
      if (!arr.includes(element)) {
        arr.push(element);
      }
    });
    return {
      ...acc,
      [filter.name]: arr,
    };
  }, {});

  const setAllCarsShow = () => {
    setShowCars(cars.map((car) => car.id));
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);

    let tab = "cars";
    if (newValue == 1) tab = "trips";
    if (newValue == 2) tab = "history";

    changeTab(tab);

    if (newValue == 2) setShowCars([]);
    else setAllCarsShow();
  };

  const onSetFilterValue = (filter, value) => {
    if (filter == "" && value == "") {
      setAllCarsShow();
      setCurrentCars(cars);
    } else if (filter != "" && value != "") {
      const filteredCars = cars.filter((car) => car[filter] === value);
      setShowCars(filteredCars.map((car) => car.id));
      setCurrentCars(filteredCars);
    }
  };

  const onTripsFilter = (startDate, endDate) => {
    if (startDate === "" || endDate === "") setCurrentTrips(trips);
    else if (startDate !== "" || endDate !== "")
      setCurrentTrips(
        trips.filter(
          (trip) => trip.start_date >= startDate && trip.end_date <= endDate
        )
      );
  };

  const carsList = departments.map((dep) => {
    const itemList = currentCars.filter((car) => car.department == dep.id);

    return (
      <CollapseItem
        key={dep.id.toString()}
        id={dep.id}
        title={dep.name}
        items={itemList}
        show={itemList.length > 0}
      />
    );
  });

  const tripsItems = currentTrips.map((trip) => (
    <TripItem key={trip.id.toString()} trip={trip} />
  ));

  const historyItems = searchHistory.map((item) => (
    <HistoryItem key={item.id.toString()} item={item} />
  ));

  return (
    <>
      <Button
        variant="contained"
        className={classes.drawerButton}
        onClick={() => setOpen(true)}
      >
        <Menu />
      </Button>
      <div className={classes.root} style={{ width: open ? "auto" : 0 }}>
        <Drawer
          open={open}
          variant="persistent"
          style={{
            height: "100vh",
          }}
        >
          <Button
            onClick={() => setOpen(false)}
            className={classes.drawerBackButton}
          >
            <ChevronLeft />
          </Button>
          <div className={classes.drawerContainer}>
            <div style={{ height: "75%" }}>
              <Paper>
                <Tabs value={value} onChange={handleChange}>
                  <Tab
                    className={classes.tab}
                    icon={<DriveEta htmlColor="#555" />}
                    value={0}
                    title={t("CARS")}
                  />
                  <Tab
                    className={classes.tab}
                    icon={<TripOrigin htmlColor="#555" />}
                    value={1}
                    title={t("TRIPS")}
                  />
                  <Tab
                    className={classes.tab}
                    icon={<History htmlColor="#555" />}
                    value={2}
                    title={t("SEARCH_HISTORY")}
                  />
                </Tabs>
              </Paper>
              <TabPanel value={value} index={0}>
                <AddPanel style={classes.addPanel} />
                <Filter
                  filters={filters}
                  values={values}
                  onSetFilterValue={onSetFilterValue}
                />
                <div className={classes.itemsList}>
                  <List>{carsList}</List>
                </div>
              </TabPanel>
              <TabPanel
                value={value}
                index={1}
                wrapperStyles={{ height: value == 1 ? "100%" : 0 }}
                boxStyles={{ height: value == 1 ? "100%" : 0 }}
              >
                <div style={{ height: "90%" }}>
                  <DateFilter onFilter={onTripsFilter} />
                  {/* <div className={classes.itemsList}> */}
                  <List className={classes.itemsList}>{tripsItems}</List>
                  {/* </div> */}
                </div>
              </TabPanel>
              <TabPanel value={value} index={2}>
                <IntervalsForm />
                <div className={classes.itemsList}>
                  {historyItems.length ? (
                    <List>{historyItems}</List>
                  ) : (
                    <Typography variant="h6" align="center">
                      {t("NO_DATA")}
                    </Typography>
                  )}
                </div>
              </TabPanel>
            </div>
            <div style={{ height: "20%" }}>
              <Footer callback={setAllCarsShow} />
            </div>
          </div>
        </Drawer>
      </div>
    </>
  );
});
